/**
 * Retry Configuration for PinPad Operations
 * 
 * Defines timeout and retry parameters for each CDP operation.
 * Each operation has specific timeout values and retry strategies
 * based on CDP PinPadWeb RETAIL v2.6.0 best practices.
 * 
 * @module config/pinpad-v2/retry-config
 */

/**
 * Operation timeout configuration (in milliseconds)
 */
export const OPERATION_TIMEOUTS = {
  // Connection operations
  CONNECT: {
    timeout: 10000,           // 10 seconds to establish connection
    description: 'Chrome extension connection timeout'
  },
  
  // Authentication operations
  LOGIN: {
    timeout: 15000,           // 15 seconds for authentication
    description: 'Login operation timeout'
  },
  
  GET_KEYS: {
    timeout: 10000,           // 10 seconds for RSA keys
    description: 'Get RSA keys timeout'
  },
  
  // Card operations
  READ_CARD: {
    timeout: 60000,           // 60 seconds - user needs time to insert card
    description: 'Card reading timeout (user interaction)'
  },
  
  GET_MERCHANT: {
    timeout: 10000,           // 10 seconds for merchant lookup
    description: 'Merchant affiliation lookup timeout'
  },
  
  // Transaction operations (CRITICAL)
  VENTA: {
    timeout: 120000,          // 120 seconds - includes user PIN entry + network
    description: 'Sale transaction timeout (CRITICAL)'
  },
  
  REIMPRESION: {
    timeout: 30000,           // 30 seconds for voucher reprint
    description: 'Voucher reprint timeout'
  }
};

/**
 * Retry strategy configuration
 */
export const RETRY_STRATEGIES = {
  // No retry - fail fast
  NONE: {
    maxRetries: 0,
    initialDelay: 0,
    maxDelay: 0,
    backoffMultiplier: 1,
    description: 'No retries, fail immediately'
  },
  
  // Quick retry - for transient errors
  QUICK: {
    maxRetries: 2,
    initialDelay: 1000,       // 1 second
    maxDelay: 3000,           // Max 3 seconds
    backoffMultiplier: 2,     // Exponential: 1s, 2s
    description: 'Quick retry for transient errors'
  },
  
  // Standard retry - for most operations
  STANDARD: {
    maxRetries: 3,
    initialDelay: 2000,       // 2 seconds
    maxDelay: 10000,          // Max 10 seconds
    backoffMultiplier: 2,     // Exponential: 2s, 4s, 8s
    description: 'Standard retry strategy'
  },
  
  // Patient retry - for user interaction operations
  PATIENT: {
    maxRetries: 2,
    initialDelay: 3000,       // 3 seconds
    maxDelay: 10000,          // Max 10 seconds
    backoffMultiplier: 2,     // Exponential: 3s, 6s
    description: 'Patient retry for user interaction'
  },
  
  // Critical retry - for financial transactions
  CRITICAL: {
    maxRetries: 2,            // Limited retries for financial ops
    initialDelay: 5000,       // 5 seconds
    maxDelay: 15000,          // Max 15 seconds
    backoffMultiplier: 2,     // Exponential: 5s, 10s
    description: 'Critical retry for financial transactions'
  }
};

/**
 * Retry configuration per operation
 */
export const OPERATION_RETRY_CONFIG = {
  CONNECT: {
    strategy: RETRY_STRATEGIES.STANDARD,
    timeout: OPERATION_TIMEOUTS.CONNECT.timeout,
    retryableErrors: ['0001', '0002', '0401'], // Connection, timeout, terminal busy
    description: 'Connection retry configuration'
  },
  
  LOGIN: {
    strategy: RETRY_STRATEGIES.QUICK,
    timeout: OPERATION_TIMEOUTS.LOGIN.timeout,
    retryableErrors: ['0100', '0002', '0401'], // Auth error, timeout, terminal busy
    description: 'Login retry configuration'
  },
  
  GET_KEYS: {
    strategy: RETRY_STRATEGIES.STANDARD,
    timeout: OPERATION_TIMEOUTS.GET_KEYS.timeout,
    retryableErrors: ['0001', '0002', '0500'], // Connection, timeout, network
    description: 'Get keys retry configuration'
  },
  
  READ_CARD: {
    strategy: RETRY_STRATEGIES.PATIENT,
    timeout: OPERATION_TIMEOUTS.READ_CARD.timeout,
    retryableErrors: ['0200', '0201', '0202', '0204'], // Card read errors
    description: 'Card reading retry configuration'
  },
  
  GET_MERCHANT: {
    strategy: RETRY_STRATEGIES.STANDARD,
    timeout: OPERATION_TIMEOUTS.GET_MERCHANT.timeout,
    retryableErrors: ['0001', '0002', '0500', '0501'], // Connection, network
    description: 'Merchant lookup retry configuration'
  },
  
  VENTA: {
    strategy: RETRY_STRATEGIES.CRITICAL,
    timeout: OPERATION_TIMEOUTS.VENTA.timeout,
    retryableErrors: ['0500', '0501'], // ONLY network errors - no card/transaction retries
    description: 'Sale transaction retry (CRITICAL - limited retries)'
  },
  
  REIMPRESION: {
    strategy: RETRY_STRATEGIES.STANDARD,
    timeout: OPERATION_TIMEOUTS.REIMPRESION.timeout,
    retryableErrors: ['0001', '0002', '0500'], // Connection, timeout, network
    description: 'Reprint retry configuration'
  }
};

/**
 * Get timeout for a specific operation
 * @param {string} operation - Operation name
 * @returns {number} Timeout in milliseconds
 */
export function getOperationTimeout(operation) {
  const config = OPERATION_TIMEOUTS[operation.toUpperCase()];
  return config ? config.timeout : 30000; // Default 30 seconds
}

/**
 * Get retry configuration for an operation
 * @param {string} operation - Operation name
 * @returns {Object} Retry configuration
 */
export function getRetryConfig(operation) {
  const config = OPERATION_RETRY_CONFIG[operation.toUpperCase()];
  
  if (!config) {
    return {
      strategy: RETRY_STRATEGIES.STANDARD,
      timeout: 30000,
      retryableErrors: []
    };
  }
  
  return config;
}

/**
 * Get retry strategy for an operation
 * @param {string} operation - Operation name
 * @returns {Object} Retry strategy configuration
 */
export function getRetryStrategy(operation) {
  const config = getRetryConfig(operation);
  return config.strategy;
}

/**
 * Check if error is retryable for a specific operation
 * @param {string} operation - Operation name
 * @param {string} errorCode - Error code
 * @returns {boolean} True if error is retryable for this operation
 */
export function isErrorRetryable(operation, errorCode) {
  const config = getRetryConfig(operation);
  return config.retryableErrors.includes(errorCode);
}

/**
 * Calculate retry delay with exponential backoff
 * @param {number} attempt - Current retry attempt (0-based)
 * @param {Object} strategy - Retry strategy configuration
 * @returns {number} Delay in milliseconds
 */
export function calculateRetryDelay(attempt, strategy) {
  const delay = strategy.initialDelay * Math.pow(strategy.backoffMultiplier, attempt);
  return Math.min(delay, strategy.maxDelay);
}

/**
 * Check if should retry based on attempt count
 * @param {number} attempt - Current retry attempt (0-based)
 * @param {string} operation - Operation name
 * @returns {boolean} True if should retry
 */
export function shouldRetry(attempt, operation) {
  const strategy = getRetryStrategy(operation);
  return attempt < strategy.maxRetries;
}

/**
 * Get operation description
 * @param {string} operation - Operation name
 * @returns {string} Operation description
 */
export function getOperationDescription(operation) {
  const config = OPERATION_RETRY_CONFIG[operation.toUpperCase()];
  return config ? config.description : 'Unknown operation';
}

/**
 * Get all retry configurations (for debugging/monitoring)
 * @returns {Object} All retry configurations
 */
export function getAllRetryConfigs() {
  return {
    timeouts: OPERATION_TIMEOUTS,
    strategies: RETRY_STRATEGIES,
    operationConfigs: OPERATION_RETRY_CONFIG
  };
}

export default OPERATION_RETRY_CONFIG;
